/**
 * ProposalAssumptions — Interactive escalation rate inputs
 *
 * Displays financial assumptions (escalation rates, degradation) as editable
 * fields. Customer can tweak rates to see how projections change.
 * Calls onRatesChange(overrides) when any rate changes.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography, TextField, InputAdornment, Button } from '@mui/material';
import { Refresh as ResetIcon } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const FIELDS = [
  { key: 'energyEscalation', label: 'Energy Escalation', group: 'always' },
  { key: 'demandEscalation', label: 'Demand Escalation', group: 'always' },
  { key: 'laborEscalation', label: 'Labor Escalation', group: 'always' },
  { key: 'degradationRate', label: 'PV Degradation', group: 'pv', readOnly: true },
  { key: 'gasDeliveryEscalation', label: 'Gas Delivery', group: 'generator' },
  { key: 'gasCommodityEscalation', label: 'Gas Commodity', group: 'generator' },
];

export default function ProposalAssumptions({ data, onRatesChange }) {
  const assumptions = data?.assumptions;
  const [overrides, setOverrides] = useState({});
  const hasOverrides = Object.keys(overrides).length > 0;

  const visibleFields = useMemo(() => {
    if (!assumptions) return [];
    return FIELDS.filter((f) => {
      if (f.group === 'always') return true;
      if (f.group === 'pv') return assumptions.hasPV;
      if (f.group === 'generator') return assumptions.hasGenerator;
      return false;
    });
  }, [assumptions]);

  const handleChange = useCallback(
    (key, rawValue) => {
      const numValue = parseFloat(rawValue);
      if (isNaN(numValue)) return;

      const newOverrides = { ...overrides, [key]: numValue };

      // If value matches original, remove the override
      if (assumptions && numValue === assumptions[key]) {
        delete newOverrides[key];
      }

      setOverrides(newOverrides);

      // Build full rates object merging originals with overrides
      if (Object.keys(newOverrides).length === 0) {
        onRatesChange(null);
      } else {
        onRatesChange({ ...assumptions, ...newOverrides });
      }
    },
    [overrides, assumptions, onRatesChange]
  );

  const handleReset = useCallback(() => {
    setOverrides({});
    onRatesChange(null);
  }, [onRatesChange]);

  if (!assumptions) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          variant="overline"
          sx={{ color: vortxColors.stone, fontWeight: 600, letterSpacing: '0.1em' }}
        >
          Financial Assumptions
        </Typography>
        {hasOverrides && (
          <Button
            size="small"
            startIcon={<ResetIcon sx={{ fontSize: '0.85rem' }} />}
            onClick={handleReset}
            sx={{
              color: vortxColors.stone,
              fontSize: '0.7rem',
              textTransform: 'none',
              '&:hover': { color: vortxColors.gold },
            }}
          >
            Reset to original
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'rgba(255,255,255,0.03)',
        }}
      >
        {visibleFields.map((field) => {
          const value = overrides[field.key] ?? assumptions[field.key];
          const isOverridden = field.key in overrides;
          return (
            <Box key={field.key} sx={{ minWidth: 120, flex: '1 1 120px', maxWidth: 160 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isOverridden ? vortxColors.gold : vortxColors.stone,
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {field.label}
              </Typography>
              {field.readOnly ? (
                <Typography
                  sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.8rem',
                    color: vortxColors.stone,
                    py: '6px',
                    px: '8px',
                  }}
                >
                  {value}%/yr
                </Typography>
              ) : (
                <TextField
                  type="number"
                  size="small"
                  value={value}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  inputProps={{
                    step: 0.1,
                    min: 0,
                    max: 20,
                    style: {
                      fontFamily: vortxTypography.mono,
                      fontSize: '0.8rem',
                      color: isOverridden ? vortxColors.gold : vortxColors.white,
                      padding: '6px 8px',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ color: vortxColors.stone, fontSize: '0.75rem' }}>%</Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      '& fieldset': {
                        borderColor: isOverridden
                          ? `${vortxColors.gold}60`
                          : 'rgba(255,255,255,0.12)',
                      },
                      '&:hover fieldset': {
                        borderColor: vortxColors.gold,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: vortxColors.gold,
                      },
                    },
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {hasOverrides && (
        <Typography
          variant="caption"
          sx={{ color: vortxColors.gold, fontSize: '0.65rem', mt: 1, display: 'block' }}
        >
          Adjusted assumptions — cashflow table below reflects your changes
        </Typography>
      )}
    </Box>
  );
}
